import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, ApiError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useAuth } from '../lib/auth';
import { Alert, Badge, Button, Loading } from '../components/ui';

// Pick tools → checkout. Pre-selects a tool if arrived via an "Unlock" button.
export default function Pricing() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const { data, loading } = useAsync(() => apiGet<{ tools: any[] }>('/api/entitlements'), []);
  const [selected, setSelected] = useState<Set<string>>(new Set(params.get('tool') ? [params.get('tool')!] : []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isOwner = me?.role === 'owner';

  if (loading) return <Loading />;
  const tools = (data?.tools || []).filter((t) => !t.owned);
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function checkout() {
    setError(null);
    setBusy(true);
    try {
      const res = await apiPost<{ url: string }>('/api/checkout', { toolIds: [...selected] });
      if (res.url) window.location.href = res.url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Checkout failed.');
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-5 py-10">
      <button onClick={() => navigate('/')} className="text-sm text-muted hover:text-soft">← Home</button>
      <h1 className="mt-3 h-display text-3xl text-soft">Unlock your tools</h1>
      <p className="mt-1 text-sm text-muted">Buy only what you need. Your CRM is always included.</p>

      {(data?.tools || []).every((t) => t.owned) ? (
        <Alert kind="success">You own every tool. Nothing left to buy.</Alert>
      ) : (
        <>
          <div className="mt-6 space-y-2">
            {tools.map((t) => (
              <button key={t.id} onClick={() => toggle(t.id)}
                className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${selected.has(t.id) ? 'border-power bg-power/10' : 'border-line hover:border-line-strong'}`}>
                <div className={`flex h-5 w-5 items-center justify-center rounded border ${selected.has(t.id) ? 'border-power bg-power text-white' : 'border-line-strong'}`}>{selected.has(t.id) ? '✓' : ''}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-head font-semibold text-soft">{t.name}</div>
                  <div className="text-xs text-muted">{t.description}</div>
                </div>
                <Badge className="border-line text-soft">{t.price_label}</Badge>
              </button>
            ))}
          </div>

          {!isOwner && <div className="mt-4"><Alert kind="info">Only the workspace owner can buy tools.</Alert></div>}
          {error && <div className="mt-4"><Alert>{error}</Alert></div>}

          <div className="mt-6">
            <Button onClick={checkout} loading={busy} disabled={!selected.size || !isOwner}>
              {selected.size ? `Check out (${selected.size})` : 'Select a tool'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
